import React from 'react'
import styled from '@emotion/styled'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { UniversalMenuConfig } from '@types'

interface MenuGenerationProfileProps {
  markdown: string | null
  isLoading: boolean
  universalMenuConfig: UniversalMenuConfig | null
}

const formatPrice = (value: number | null) =>
  value != null ? `$${value}` : '—'

const formatPercent = (value: number | null) =>
  value != null ? `${value}%` : '—'

const ConfigSummary: React.FC<{ config: UniversalMenuConfig }> = ({
  config,
}) => {
  const activeVendors = config.vendorBlacklist.filter((v) => v.active)
  const activeItems = config.menuItemBlacklist.filter((i) => i.active)

  return (
    <ConfigSection>
      <ConfigHeader>Universal Menu Config</ConfigHeader>
      <ConfigRow>
        <ConfigLabel>Sides:</ConfigLabel>
        <ConfigValue>{config.enableSides ? 'Enabled' : 'Disabled'}</ConfigValue>
      </ConfigRow>
      <ConfigRow>
        <ConfigLabel>Mods:</ConfigLabel>
        <ConfigValue>
          {config.enableModifications ? 'Enabled' : 'Disabled'}
        </ConfigValue>
      </ConfigRow>
      <ConfigRow>
        <ConfigLabel>Max Item Price:</ConfigLabel>
        <ConfigValue>{formatPrice(config.maxItemPriceLimit)}</ConfigValue>
      </ConfigRow>
      <ConfigRow>
        <ConfigLabel>Max Total Price:</ConfigLabel>
        <ConfigValue>{formatPrice(config.maxTotalItemPriceLimit)}</ConfigValue>
      </ConfigRow>
      <ConfigRow>
        <ConfigLabel>Max Mod Price:</ConfigLabel>
        <ConfigValue>
          {formatPrice(config.maxModificationPriceLimit)}
        </ConfigValue>
      </ConfigRow>
      <ConfigRow>
        <ConfigLabel>Min Margin:</ConfigLabel>
        <ConfigValue>
          {formatPercent(config.flatPriceMarginMinimum)}
        </ConfigValue>
      </ConfigRow>
      <ConfigRow>
        <ConfigLabel>Dietary Exclusions:</ConfigLabel>
        <ConfigValue>
          {config.excludedDietaryTags.length > 0
            ? config.excludedDietaryTags.join(', ')
            : 'None'}
        </ConfigValue>
      </ConfigRow>
      <ConfigRow>
        <ConfigLabel>Vendor Blacklist:</ConfigLabel>
        <ConfigValue>
          {activeVendors.length > 0
            ? activeVendors.map((v) => v.vendorName).join(', ')
            : 'None'}
        </ConfigValue>
      </ConfigRow>
      <ConfigRow>
        <ConfigLabel>Item Blacklist:</ConfigLabel>
        <ConfigValue>
          {activeItems.length > 0
            ? activeItems.map((i) => `${i.itemName} (${i.chefName})`).join(', ')
            : 'None'}
        </ConfigValue>
      </ConfigRow>
    </ConfigSection>
  )
}

const MenuGenerationProfile: React.FC<MenuGenerationProfileProps> = ({
  markdown,
  isLoading,
  universalMenuConfig,
}) => {
  return (
    <Container>
      <Content>
        {universalMenuConfig && <ConfigSummary config={universalMenuConfig} />}
        {isLoading ? (
          <EmptyState>Loading profile...</EmptyState>
        ) : markdown ? (
          <MarkdownWrapper>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {markdown}
            </ReactMarkdown>
          </MarkdownWrapper>
        ) : (
          <EmptyState>
            No client profile found. You can ask the assistant to create one.
          </EmptyState>
        )}
      </Content>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`

const ConfigSection = styled.div`
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
`

const ConfigHeader = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`

const ConfigRow = styled.div`
  display: flex;
  font-size: 12px;
  line-height: 1.6;
  color: #333;
`

const ConfigLabel = styled.span`
  font-weight: 500;
  margin-right: 4px;
  white-space: nowrap;
`

const ConfigValue = styled.span`
  color: #555;
`

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  color: #888;
  font-size: 13px;
  text-align: center;
`

const MarkdownWrapper = styled.div`
  font-size: 13px;
  line-height: 1.6;
  color: #333;

  h1,
  h2,
  h3,
  h4 {
    margin-top: 16px;
    margin-bottom: 8px;
    &:first-of-type {
      margin-top: 0;
    }
  }

  h1 {
    font-size: 18px;
    font-weight: bold;
  }
  h2 {
    font-size: 16px;
    font-weight: bold;
  }
  h3 {
    font-size: 14px;
    font-weight: bold;
  }

  p {
    margin: 8px 0;
  }

  ul,
  ol {
    padding-left: 20px;
    margin: 8px 0;
  }

  li {
    margin: 4px 0;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 8px 0;
    font-size: 12px;
  }

  th,
  td {
    border: 1px solid #ddd;
    padding: 6px 8px;
    text-align: left;
  }

  th {
    background: #f5f5f5;
    font-weight: 600;
  }

  code {
    background: #f5f5f5;
    padding: 2px 4px;
    border-radius: 3px;
    font-size: 12px;
  }

  hr {
    border: none;
    border-top: 1px solid #e0e0e0;
    margin: 16px 0;
  }
`

export default MenuGenerationProfile
